import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
	Accordion,
	AccordionItem,
	AccordionButton,
	AccordionPanel,
	AccordionIcon,
	Flex,
	Box,
	FormControl,
	Input,
	Textarea,
	Stack,
	Select,
	Button,
	useToast,
	Text,
	useDisclosure,
	Table,
	Thead,
	Tr,
	Th,
	Tbody,
	Td,
	VStack,
} from "@chakra-ui/react";
import moment from "moment";
import { FiFileText, FiInfo, FiMessageSquare, FiSend } from "react-icons/fi";

import { ModalFieldLabel, ModalFooterActions, PropertyModalSection, PropertyModalShell } from "./shared";
import { addMessageAsync } from "../../redux/user/userSlice";
import MessagesTableRow from "../TableRows/MessagesTableRow";

export default function MessagesTab({ property }) {
	const { t } = useTranslation();
	const isArchived = property.archived;
	const initialMessageInformation = {
		propertyId: property.id,
		author: {},
		title: "",
		description: "",
		type: "owner",
	};
	const [messageInformation, setMessageInformation] = useState(initialMessageInformation);
	const messages = property.messages || [];
	const { isOpen, onOpen, onClose } = useDisclosure();
	const user = useSelector((state) => state.user.user);

	const dispatch = useDispatch();
	const toast = useToast();

	const handleCloseModal = () => {
		onClose();
		setMessageInformation(initialMessageInformation);
	};

	const handleSendNotification = () => {
		const payload = { ...messageInformation, author: { name: user.name, email: user.email } };
		console.log("sending message:", payload);
		dispatch(addMessageAsync(payload))
			.then((r) => {
				console.log(r);
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Mass notification sent!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
					setMessageInformation({});
				}
			})
			.finally(() => {
				onClose();
				setMessageInformation(initialMessageInformation);
			});
	};

	const handleNotificationInfoChange = (e) => {
		console.log(e.target.value);
		setMessageInformation({ ...messageInformation, [e.target.name]: e.target.value });
		console.log("sending notification:", messageInformation);
	};

	useEffect(() => {
		console.log(messageInformation);
	}, [messageInformation]);

	const renderSendMessageModal = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={handleCloseModal}
			size="3xl"
			icon={FiMessageSquare}
			title={t("property.sendMessage")}
			subtitle={t("property.sendMessageSubtitle")}
			footer={
				<ModalFooterActions
					onCancel={handleCloseModal}
					onConfirm={handleSendNotification}
					confirmLabel={t("misc.send")}
					confirmIcon={FiSend}
				/>
			}
		>
			<PropertyModalSection icon={FiInfo} title={t("property.messageInformation")}>
				<VStack spacing={3} align="stretch">
					<FormControl>
						<ModalFieldLabel icon={FiFileText}>{t("table.title")}</ModalFieldLabel>
						<Input
							size="sm"
							type="text"
							placeholder={t("property.titlePlaceholder")}
							name="title"
							onChange={handleNotificationInfoChange}
							borderRadius="md"
						/>
					</FormControl>
					<FormControl>
						<ModalFieldLabel icon={FiMessageSquare}>{t("table.description")}</ModalFieldLabel>
						<Textarea
							size="sm"
							placeholder={t("property.descriptionPlaceholder")}
							name="description"
							onChange={handleNotificationInfoChange}
							borderRadius="md"
							rows={4}
						/>
					</FormControl>
					<FormControl>
						<ModalFieldLabel icon={FiInfo}>{t("table.type")}</ModalFieldLabel>
						<Select size="sm" name="type" onChange={handleNotificationInfoChange} borderRadius="md" defaultValue="owner">
							<option value="owner">{t("table.owner")}</option>
							<option value="tenant">{t("table.tenant")}</option>
						</Select>
					</FormControl>
				</VStack>
			</PropertyModalSection>
		</PropertyModalShell>
	);

	return (
		<Stack>
			<Stack direction={"row"}>
				<Button onClick={onOpen} colorScheme="blue" width={"fit-content"} isDisabled={isArchived}>
					{t("property.sendMessage")}
				</Button>

				{renderSendMessageModal()}
			</Stack>
			<Table variant="simple" size="sm">
				<Thead>
					<Tr my=".8rem" pl="0px">
						<Th>{t("table.lastMessage")}</Th>
						<Th>{t("table.owner")}</Th>
						<Th>{t("table.title")}</Th>
						<Th>{t("table.author")}</Th>
						<Th>{t("table.type")}</Th>
						<Th>{t("table.date")}</Th>
						<Th></Th>
					</Tr>
				</Thead>
				<Tbody>
					{messages?.map((t, index) => (
						<MessagesTableRow key={index} row={t} author={{ name: user.name, email: user.email }} property={property} />
					))}
					{messages?.length === 0 && (
						<Tr>
							<Td>{t("table.noData")}</Td>
						</Tr>
					)}
				</Tbody>
			</Table>
			<Box hidden>
				<Accordion allowMultiple width="100%">
					{messages
						.slice()
						.reverse()
						.map((n, index) => (
							<AccordionItem key={index}>
								<AccordionButton>
									<Flex flex="1" direction={"row"} alignItems={"center"} whiteSpace={"pre"} justifyContent={"space-between"}>
										<Text fontSize="md">{n.title} </Text>
										<Text fontSize={"small"}>~ {moment(n.timestamp).format("DD MMM YYYY - HH:mm")}</Text>
									</Flex>
									<AccordionIcon />
								</AccordionButton>

								<AccordionPanel pb={4}>
									<Stack spacing={2}>
										<Box>
											<Text fontSize={"large"}>Message:</Text>
											<Text>{n.description}</Text>
										</Box>
									</Stack>
								</AccordionPanel>
							</AccordionItem>
						))}
				</Accordion>
			</Box>
		</Stack>
	);
}
